import { Chip, IconButton, Stack, TableCell, TableRow, Tooltip } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useDialog } from "lib/@hooks/useDialog"
import { If } from "lib/@components/switch"
import { confirm } from "lib/@dialogs/confirm"
import { alert } from "lib/@dialogs/alert"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import {
    updateFentraUser,
    deactivateFentraUser,
    reactivateFentraUser,
    deleteFentraUser,
    linkFentraUser,
} from "routes/qa/fentra/controllers/fentra-mutations"
import { FentraUserDialog } from "routes/qa/fentra/users/fentra-user-dialog"

export function FentraUserRow({ user }) {
    const openUserDialog = useDialog(<FentraUserDialog />, { maxWidth: "sm", fullWidth: true })
    const isUnlinked = user?.provisionStatus === "unlinked"

    return (
        <TableRow hover>
            <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{user?.email}</TableCell>
            <TableCell>{user?.displayName}</TableCell>
            <TableCell>{user?.jobTitle || "—"}</TableCell>
            <TableCell>
                <Chip
                    size="small"
                    label={user?.active ? "Active" : "Inactive"}
                    color={user?.active ? "success" : "default"}
                    variant="outlined"
                />
            </TableCell>
            <TableCell>
                <Chip size="small" label={user?.provisionStatus ?? "—"} variant="outlined" />
            </TableCell>
            <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={handleEdit}>
                            <Iconify icon="mdi:pencil" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={user?.active ? "Deactivate" : "Reactivate"}>
                        <IconButton size="small" onClick={handleToggleActive}>
                            <Iconify icon={user?.active ? "mdi:account-off" : "mdi:account-check"} />
                        </IconButton>
                    </Tooltip>
                    {/* Link button is only relevant when this user exists in Fentra but has no
                        corresponding SCIM externalId — pushing the link triggers a SCIM CREATE */}
                    <If value={isUnlinked}>
                        <Tooltip title="Link (push to SCIM)">
                            <IconButton size="small" onClick={handleLink}>
                                <Iconify icon="mdi:link-variant" />
                            </IconButton>
                        </Tooltip>
                    </If>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={handleDelete} color="error">
                            <Iconify icon="mdi:delete" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </TableCell>
        </TableRow>
    )

    async function handleEdit() {
        const data = await openUserDialog({ mode: "edit", user })
        if (!data) return
        const result = await updateFentraUser({ id: user._id, ...data })
        if (!result?.ok) {
            const msg = result?.scimStatus
                ? `Update failed (SCIM ${result.scimStatus}): ${result.error}`
                : `Update failed: ${result?.error ?? "Unknown error"}`
            await alert(msg, "Update User")
            return
        }
        invalidate("fentraUsers")
        successSnackbar("User updated")
    }

    async function handleToggleActive() {
        if (!user) return
        try {
            const result = user.active
                ? await deactivateFentraUser({ id: user._id })
                : await reactivateFentraUser({ id: user._id })
            if (!result?.ok) {
                errorSnackbar(result?.error ?? "Operation failed")
                return
            }
            invalidate("fentraUsers")
        } catch (err) {
            errorSnackbar(err?.message || "Operation failed")
        }
    }

    async function handleLink() {
        if (!user) return
        try {
            const result = await linkFentraUser({ id: user._id })
            if (!result?.ok) {
                const msg = result?.scimStatus
                    ? `Link failed (SCIM ${result.scimStatus}): ${result.error}`
                    : `Link failed: ${result?.error ?? "Unknown error"}`
                await alert(msg, "Link User")
                return
            }
            invalidate("fentraUsers")
            successSnackbar("User linked via SCIM")
        } catch (err) {
            errorSnackbar(err?.message || "Link failed")
        }
    }

    async function handleDelete() {
        if (!user) return
        if (!(await confirm(`Delete user "${user.displayName || user.email}"?`, "Delete User"))) return
        try {
            await deleteFentraUser({ id: user._id })
            invalidate("fentraUsers")
            successSnackbar("User deleted")
        } catch (err) {
            errorSnackbar(err?.message || "Delete failed")
        }
    }
}
